// Trombinoscope de l'équipe : fiches membres (photo, pôle, fonction, anniversaire, contact)
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Upload, Phone, Cake, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { POLES, ROLES } from "@/lib/constants";

const MOIS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];

const FicheDialog = ({ membre, open, onClose, onSaved }) => {
  const [form, setForm] = useState({});
  const [fichier, setFichier] = useState(null);
  const [envoi, setEnvoi] = useState(false);

  useEffect(() => {
    if (!open || !membre) return;
    setFichier(null);
    setForm({
      name: membre.name || "", pole: membre.pole || "", fonction: membre.fonction || "",
      telephone: membre.telephone || "", bio: membre.bio || "",
      anniversaire_jour: membre.anniversaire_jour || "", anniversaire_mois: membre.anniversaire_mois || "",
      annee_arrivee: membre.annee_arrivee || "",
    });
  }, [open, membre]);

  const sauver = async (e) => {
    e.preventDefault();
    setEnvoi(true);
    try {
      const payload = {
        name: form.name, pole: form.pole || null, fonction: form.fonction,
        telephone: form.telephone, bio: form.bio,
        anniversaire_jour: form.anniversaire_jour ? Number(form.anniversaire_jour) : null,
        anniversaire_mois: form.anniversaire_mois ? Number(form.anniversaire_mois) : null,
        annee_arrivee: form.annee_arrivee ? Number(form.annee_arrivee) : null,
      };
      await api.patch(`/users/${membre.id}/profile`, payload);
      if (fichier) {
        const fd = new FormData();
        fd.append("fichier", fichier);
        await api.post(`/users/${membre.id}/photo`, fd);
      }
      toast.success("Fiche mise à jour");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="profile-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Fiche de {membre?.name}</DialogTitle>
          <DialogDescription>Ces informations sont visibles par les membres de l'équipe.</DialogDescription>
        </DialogHeader>
        <form onSubmit={sauver} className="space-y-4">
          <div>
            <Label>Nom affiché</Label>
            <Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl mt-1.5" data-testid="profile-name-input" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Pôle</Label>
              <Select value={form.pole || "aucun"} onValueChange={(v) => setForm({ ...form, pole: v === "aucun" ? "" : v })}>
                <SelectTrigger className="rounded-xl mt-1.5" data-testid="profile-pole-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="aucun">Non précisé</SelectItem>
                  {Object.entries(POLES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Fonction</Label>
              <Input value={form.fonction || ""} onChange={(e) => setForm({ ...form, fonction: e.target.value })} placeholder="Ex. Photographe, Danseuse" className="rounded-xl mt-1.5" data-testid="profile-fonction-input" />
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <Label>Jour de naissance</Label>
              <Input type="number" min={1} max={31} value={form.anniversaire_jour || ""} onChange={(e) => setForm({ ...form, anniversaire_jour: e.target.value })} className="rounded-xl mt-1.5" data-testid="profile-jour-input" />
            </div>
            <div>
              <Label>Mois</Label>
              <Select value={String(form.anniversaire_mois || "")} onValueChange={(v) => setForm({ ...form, anniversaire_mois: v })}>
                <SelectTrigger className="rounded-xl mt-1.5" data-testid="profile-mois-select"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  {MOIS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Arrivée dans l'ASBL</Label>
              <Input type="number" min={1990} max={2100} value={form.annee_arrivee || ""} onChange={(e) => setForm({ ...form, annee_arrivee: e.target.value })} placeholder="2019" className="rounded-xl mt-1.5" data-testid="profile-annee-input" />
            </div>
          </div>
          <div>
            <Label>Téléphone</Label>
            <Input value={form.telephone || ""} onChange={(e) => setForm({ ...form, telephone: e.target.value })} placeholder="+32 …" className="rounded-xl mt-1.5" data-testid="profile-telephone-input" />
          </div>
          <div>
            <Label>Présentation (400 caractères max)</Label>
            <Textarea rows={3} maxLength={400} value={form.bio || ""} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="rounded-xl mt-1.5" data-testid="profile-bio-input" />
          </div>
          <div>
            <Label>Photo</Label>
            <label className="mt-1.5 flex items-center gap-3 rounded-xl border border-dashed border-border px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors">
              <Upload className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground truncate">{fichier ? fichier.name : "Choisir une photo (5 Mo max)"}</span>
              <input type="file" accept="image/*" className="hidden" onChange={(e) => setFichier(e.target.files?.[0] || null)} data-testid="profile-photo-input" />
            </label>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={envoi} className="rounded-full" data-testid="profile-save-button">
              {envoi ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function Team() {
  const { user } = useAuth();
  const [membres, setMembres] = useState([]);
  const [fiche, setFiche] = useState(null);

  const charger = useCallback(async () => {
    try {
      const { data } = await api.get("/users");
      setMembres(data.filter((m) => m.statut === "actif"));
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);
  useEffect(() => { charger(); }, [charger]);

  const moisCourant = new Date().getMonth() + 1;
  const anniversaires = membres
    .filter((m) => m.anniversaire_mois === moisCourant && m.anniversaire_jour)
    .sort((a, b) => a.anniversaire_jour - b.anniversaire_jour);

  return (
    <div className="space-y-8" data-testid="team-page">
      <div>
        <h1 className="font-serif text-4xl font-bold">L'équipe</h1>
        <p className="text-muted-foreground text-sm mt-1">Qui fait quoi chez Inyange — et quand souhaiter un bon anniversaire</p>
      </div>

      {anniversaires.length > 0 && (
        <div className="rounded-2xl border border-accent/40 bg-accent/15 px-5 py-4" data-testid="team-birthdays">
          <p className="text-xs font-semibold uppercase tracking-wide inline-flex items-center gap-1.5">
            <Cake className="h-3.5 w-3.5" /> Anniversaires en {MOIS[moisCourant - 1]}
          </p>
          <p className="text-sm mt-1">
            {anniversaires.map((m) => `${m.name} (le ${m.anniversaire_jour})`).join(" · ")}
          </p>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="team-list">
        {membres.map((m) => {
          const modifiable = user.role === "admin" || m.id === user.id;
          const initiales = (m.name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
          return (
            <div key={m.id} className="bg-card rounded-2xl border border-border overflow-hidden carte-douce apparition" data-testid={`team-card-${m.id}`}>
              <div className="aspect-[4/3] bg-muted grid place-items-center overflow-hidden">
                {m.photo_url ? (
                  <img src={m.photo_url} alt={m.name} className="h-full w-full object-cover" data-testid={`team-photo-${m.id}`} />
                ) : (
                  <span className="font-serif text-5xl text-muted-foreground/50">{initiales}</span>
                )}
              </div>
              <div className="p-5">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h2 className="font-serif text-xl font-semibold">{m.name}</h2>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {m.fonction || ROLES[m.role] || m.role}
                      {m.annee_arrivee ? ` · depuis ${m.annee_arrivee}` : ""}
                    </p>
                  </div>
                  {modifiable && (
                    <button onClick={() => setFiche(m)} className="p-2 rounded-full text-muted-foreground hover:bg-muted transition-colors" data-testid={`team-edit-${m.id}`}>
                      <Pencil className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {m.pole && <Badge variant="outline" className="text-[10px]">{POLES[m.pole]}</Badge>}
                  {m.pole_responsable && <Badge className="text-[10px] bg-primary/15 text-primary border-0">Responsable {POLES[m.pole_responsable]}</Badge>}
                  {m.role === "admin" && <Badge className="text-[10px] bg-secondary text-secondary-foreground border-0">Admin</Badge>}
                </div>
                {m.bio && <p className="text-sm text-muted-foreground mt-3">{m.bio}</p>}
                <div className="mt-4 pt-3 border-t border-border/60 space-y-1.5 text-xs text-muted-foreground">
                  <p className="inline-flex items-center gap-1.5 break-all"><Mail className="h-3 w-3" /> {m.email}</p>
                  {m.telephone && <p className="inline-flex items-center gap-1.5"><Phone className="h-3 w-3" /> {m.telephone}</p>}
                  {m.anniversaire_jour && m.anniversaire_mois && (
                    <p className="inline-flex items-center gap-1.5" data-testid={`team-birthday-${m.id}`}>
                      <Cake className="h-3 w-3" /> {m.anniversaire_jour} {MOIS[m.anniversaire_mois - 1]}
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <FicheDialog membre={fiche} open={Boolean(fiche)} onClose={() => setFiche(null)} onSaved={charger} />
    </div>
  );
}
